import { z } from 'zod'
import { timestampSchema, uuidSchema } from './common.js'

const vaultPathSchema = z
  .string()
  .min(1)
  .max(1024)
  .refine(
    (p) => !p.includes('..') && !p.startsWith('/'),
    'Pfad darf keine Parent-Traversierung oder absolute Pfade enthalten',
  )

export const vaultFileSchema = z.object({
  id: uuidSchema,
  workspace_id: uuidSchema,
  path: vaultPathSchema,
  content: z.string(),
  content_type: z.string().default('text/markdown'),
  // Older generated/inline rows can have no recorded size after migration 026.
  // Unknown is not a zero-byte file.
  size_bytes: z.number().int().nonnegative().nullish().transform((value) => value ?? undefined),
  frontmatter: z.record(z.string(), z.unknown()).default({}),
  tags: z.array(z.string()).default([]),
  outgoing_links: z.array(z.string()).default([]),
  // B2.0-R3: first-class Image-Felder. Bei Non-Image-Rows null/{}.
  storage_path: z.string().nullable().default(null),
  image_metadata: z.record(z.string(), z.unknown()).default({}),
  created_by: uuidSchema.nullable(),
  updated_by: uuidSchema.nullable(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
})
export type VaultFile = z.infer<typeof vaultFileSchema>

export const vaultFileWriteSchema = z.object({
  path: vaultPathSchema,
  content: z.string(),
  content_type: z.string().optional(),
  frontmatter: z.record(z.string(), z.unknown()).optional(),
  tags: z.array(z.string()).optional(),
})
export type VaultFileWrite = z.infer<typeof vaultFileWriteSchema>

export const vaultFileMoveSchema = z.object({
  from: vaultPathSchema,
  to: vaultPathSchema,
})
export type VaultFileMove = z.infer<typeof vaultFileMoveSchema>

export const vaultFileVersionSchema = z.object({
  id: uuidSchema,
  file_id: uuidSchema,
  workspace_id: uuidSchema,
  content: z.string(),
  content_hash: z.string(),
  saved_by: uuidSchema.nullable(),
  saved_at: timestampSchema,
})
export type VaultFileVersion = z.infer<typeof vaultFileVersionSchema>

export const vaultBulkUploadResultItemSchema = z.object({
  filename: z.string(),
  path: z.string().optional(),
  status: z.enum(['created', 'overwritten', 'skipped', 'error']),
  message: z.string().optional(),
  parserError: z.string().nullable().optional(),
  ingestionJobId: z.string().nullable().optional(),
  ingestionStatus: z
    .enum(['pending', 'processing', 'completed', 'completed_with_warnings', 'failed', 'cancelled'])
    .nullable()
    .optional(),
  ingestionError: z.string().nullable().optional(),
})

export const vaultBulkUploadResponseSchema = z.object({
  results: z.array(vaultBulkUploadResultItemSchema),
})

export type VaultBulkUploadResultItem = z.infer<typeof vaultBulkUploadResultItemSchema>
export type VaultBulkUploadResponse = z.infer<typeof vaultBulkUploadResponseSchema>
